import { useMemo, useRef, useState } from 'react'
import { User } from 'lucide-react'
import Icon from './Icon.jsx'
import ContextMenu, { ContextMenuItem, ContextMenuLabel, ContextMenuSeparator } from './ContextMenu.jsx'
import ListEntryEditor from './ListEntryEditor.jsx'
import { formatDate, formatShort, isoWeekday, monthOf, weekdayName } from '../lib/dates.js'
import { tintOf } from '../lib/theme.js'
import { useGridKeys, findCell, focusCell } from '../lib/useGridKeys.js'

/**
 * A list, drawn as a calendar of days with people in the cells.
 *
 * This is the board turned round. On /board a person has a row and the job is what gets
 * written in the cell; here the DAY has the cell and the people are what gets written in
 * it, each with a note if one is wanted. So the grid is weeks down and weekdays across —
 * one row for the week view, two for the fortnight, six for the month, and a single wide
 * cell for the day — and every one of those is the same table with a different set of
 * rows, so the gestures below work the same on all four.
 *
 * The gestures are the board's, with the OS convention the board's own comment cites and
 * then completes: a plain drag MOVES somebody to another day, and the same drag with Ctrl
 * (⌘ on a Mac) held COPIES them there instead, read off the drop itself so the decision can
 * be made mid-drag the way it can in Explorer or Finder. Right-click a chip for Copy, Cut,
 * the note and Remove; right-click a day for Paste and Add. Pasting a cut moves; pasting a
 * copy adds. Click a day's empty space to add somebody, click a chip to change their note.
 * The table is one tab stop and the arrows walk the days (lib/useGridKeys.js).
 *
 * A <table>, not a CSS grid, for that last reason: useGridKeys reads rows and cells off the
 * table's own structure, and a list should be driven from the keyboard exactly the way the
 * board is.
 */

const isCopyDrag = (e) => e.ctrlKey || e.metaKey

/**
 * Whether this day would take the drop — the same test for the highlight and the drop, so a
 * day never lights up and then refuses. Onto the day the chip is already on is nothing; onto
 * a day the person is already on is a duplicate the server would refuse anyway.
 */
function canDropOn(dragging, date, entriesOn, writable) {
  if (!dragging || !writable) return false
  if (dragging.date === date) return false
  return !entriesOn(date).some((e) => e.userId === dragging.userId)
}

const VIEW_LABEL = { day: 'Day', week: 'Week', '2week': '2 weeks', month: 'Month' }
export const LIST_VIEWS = Object.keys(VIEW_LABEL)
export const listViewLabel = (v) => VIEW_LABEL[v]

export default function ListGrid({
  board, // the /api/lists/:id/board payload
  weeks, // [[iso × 7], …] — or [[iso]] for the day view
  view, // 'day' | 'week' | '2week' | 'month'
  today,
  month = null, // 'YYYY-MM' the month view is about; days outside it are dimmed
  readOnly = false,
  clip = null, // { userId, name, note, cutEntry } | null — a copied or cut person
  onCopyEntry, // (clip | null) => void
  onAdd, // ({ date, userId, note }) => Promise<boolean>
  onSaveNote, // (entry, note) => Promise<boolean>
  onMove, // (entry, toDate) => Promise<boolean>
  onCopy, // (entry, toDate) => Promise<boolean>
  onRemove, // (entry) => Promise<boolean>
  onPickDay = null, // (iso) => void — a date opens that day
}) {
  // Both halves: the caller's read-only switch, and the server's answer for this list.
  const writable = !readOnly && Boolean(board.canWrite)
  const colour = board.list?.color || null
  const peopleById = useMemo(() => new Map((board.people || []).map((p) => [p.id, p])), [board.people])
  const holidayOn = useMemo(() => new Map((board.days || []).map((d) => [d.date, d.holiday])), [board.days])

  const byDate = useMemo(() => {
    const m = new Map()
    for (const e of board.entries || []) {
      if (!m.has(e.date)) m.set(e.date, [])
      m.get(e.date).push(e)
    }
    for (const rows of m.values()) rows.sort((a, b) => a.sortOrder - b.sortOrder)
    return m
  }, [board.entries])
  const entriesOn = (date) => byDate.get(date) || []
  // The name travels on the entry so a person who has since left still reads as themselves.
  const nameOf = (e) => e.userName || peopleById.get(e.userId)?.name || 'Somebody'

  // ── Local state: what is mid-drag, which day it is over, the open menu, the open editor.
  // The data itself is the caller's; every gesture ends in one call up.
  const [dragging, setDragging] = useState(null) // { id, userId, date, entry } | null
  const [dropDate, setDropDate] = useState(null)
  const [menu, setMenu] = useState(null) // { x, y, kind: 'day' | 'chip', date, entry? } | null
  const [editCtx, setEditCtx] = useState(null) // { mode: 'add' | 'edit', date, entry?, el } | null
  // The editor is a Popover, which wants a ref to its anchor; the anchor is whichever cell
  // or chip was clicked, so the ref is re-pointed rather than re-created.
  const anchorRef = useRef(null)
  anchorRef.current = editCtx?.el || null
  const gridKeys = useGridKeys(!readOnly)

  const closeEditor = () => {
    const key = editCtx?.date
    setEditCtx(null)
    // Back onto the day the editor was over, so the arrow keys carry on from there.
    focusCell(key)
  }
  const openAdd = (date, el) => {
    if (!writable || !el) return
    setEditCtx({ mode: 'add', date, el })
  }
  const openEdit = (entry, el) => {
    if (!writable || !el) return
    setEditCtx({ mode: 'edit', date: entry.date, entry, el })
  }

  const openDayMenu = (e, date) => {
    if (!writable) return
    e.preventDefault()
    setMenu({ x: e.clientX, y: e.clientY, kind: 'day', date })
  }
  const openChipMenu = (e, entry) => {
    // A reader gets no menu at all: copying is only worth offering where pasting is.
    if (!writable) return
    e.preventDefault()
    e.stopPropagation()
    setMenu({ x: e.clientX, y: e.clientY, kind: 'chip', date: entry.date, entry })
  }

  const copyChip = (entry, cut = false) =>
    onCopyEntry?.({ userId: entry.userId, name: nameOf(entry), note: entry.note, cutEntry: cut ? entry : null })

  /** Pasting a cut moves the row; pasting a copy adds a new one. Either way the clip is spent. */
  const paste = (date) => {
    if (!clip) return
    if (clip.cutEntry) onMove(clip.cutEntry, date)
    else onAdd({ date, userId: clip.userId, note: clip.note || '' })
    onCopyEntry?.(null)
  }
  const canPasteOn = (date) =>
    writable && Boolean(clip) && !entriesOn(date).some((e) => e.userId === clip.userId)

  // Only people not already on the day being added to.
  const availableOn = (date) => {
    const taken = new Set(entriesOn(date).map((e) => e.userId))
    return (board.people || []).filter((p) => !taken.has(p.id))
  }

  const chipEl = (id) => document.querySelector(`[data-entry-id="${id}"]`)

  const renderChip = (entry) => {
    // Title and phone arrive on an entry only when this list shows them — the server leaves
    // them out otherwise (routes/lists.js, shapeEntryFor) — so the chip draws what the entry
    // carries and needs no switch of its own. The switch and the values come in the same
    // response, so there is no moment where one is fresh and the other is not.
    const title = entry.userTitle || null
    const phone = entry.userPhone || null
    const isCut = clip?.cutEntry?.id === entry.id
    const isDragging = dragging?.id === entry.id
    const menuOpen = menu?.kind === 'chip' && menu.entry?.id === entry.id
    const editing = editCtx?.mode === 'edit' && editCtx.entry?.id === entry.id
    return (
      <div
        key={entry.id}
        data-entry-id={entry.id}
        className={`list-chip ${writable ? 'editable' : ''} ${isCut ? 'cut' : ''} ${
          isDragging ? 'dragging' : ''
        } ${menuOpen ? 'menu-open' : ''} ${editing ? 'editing' : ''}`}
        // The note is what hovering surfaces; the how-to hint only where there is no note
        // and something can be done about it.
        title={entry.note || (writable ? 'Click for a note · drag to move · Ctrl-drag to copy' : undefined)}
        draggable={writable}
        onDragStart={(e) => {
          e.dataTransfer.setData('text/plain', nameOf(entry))
          e.dataTransfer.effectAllowed = 'copyMove'
          setDragging({ id: entry.id, userId: entry.userId, date: entry.date, entry })
        }}
        // An abandoned drag still fires dragend, so nothing can be left lit.
        onDragEnd={() => { setDragging(null); setDropDate(null) }}
        onContextMenu={(e) => openChipMenu(e, entry)}
        onClick={(e) => { e.stopPropagation(); openEdit(entry, e.currentTarget) }}
      >
        <User size={14} strokeWidth={2} className="list-chip-icon" aria-hidden="true" />
        <span className="list-chip-text">
          <span className="list-chip-name">{nameOf(entry)}</span>
          {(title || phone) && (
            <span className="list-chip-meta">
              {title}
              {title && phone && ' · '}
              {/* Its own element so a narrow day wraps before the number, never inside it. */}
              {phone && <span className="list-chip-phone">{phone}</span>}
            </span>
          )}
          {entry.note && <span className="list-chip-note">{entry.note}</span>}
        </span>
      </div>
    )
  }

  const renderCell = (date) => {
    const entries = entriesOn(date)
    const holiday = holidayOn.get(date)
    const outside = month ? monthOf(date) !== month : false
    const canDrop = canDropOn(dragging, date, entriesOn, writable)
    const isDrop = dropDate === date && canDrop
    const isEditing = editCtx?.mode === 'add' && editCtx.date === date
    const menuOpen = menu?.kind === 'day' && menu.date === date
    const dayNumber = String(Number(date.slice(8)))
    const isFirst = date.slice(8) === '01'
    return (
      <td
        key={date}
        // Published so the arrows can walk the days (lib/useGridKeys.js) and the editor can
        // put focus back where it was.
        data-cell-key={date}
        // Focusable by click and by the arrows, never by Tab — the table is the one tab stop.
        tabIndex={readOnly ? undefined : -1}
        className={[
          'list-cell',
          outside ? 'outside' : '',
          isoWeekday(date) >= 6 ? 'weekend' : '',
          date === today ? 'today' : '',
          holiday?.observed ? 'holiday' : '',
          writable && entries.length === 0 ? 'empty-cell' : '',
          // Room on the right for the + strip, so no chip sits underneath it.
          writable ? 'has-add' : '',
          isDrop ? 'drop-target' : '',
          isEditing ? 'editing' : '',
          menuOpen ? 'menu-open' : '',
          writable ? '' : 'readonly',
        ].filter(Boolean).join(' ')}
        // The day's own space, not the chips sitting on it — a click on one of those is
        // about that person and opens their note instead.
        onClick={(e) => {
          if (e.target.closest('.list-chip, .cell-add, .list-cell-date')) return
          openAdd(date, e.currentTarget)
        }}
        onContextMenu={(e) => {
          if (e.target.closest('.list-chip')) return
          openDayMenu(e, date)
        }}
        onDragOver={(e) => {
          if (!canDrop) return
          e.preventDefault()
          e.dataTransfer.dropEffect = isCopyDrag(e) ? 'copy' : 'move'
          if (dropDate !== date) setDropDate(date)
        }}
        onDragLeave={(e) => {
          // dragleave also fires on crossing into a CHILD of this cell, which would flicker
          // the highlight off and on for the whole drag.
          if (e.currentTarget.contains(e.relatedTarget)) return
          setDropDate((d) => (d === date ? null : d))
        }}
        onDrop={(e) => {
          if (!canDrop) return
          e.preventDefault()
          const d = dragging
          setDragging(null)
          setDropDate(null)
          if (isCopyDrag(e)) onCopy(d.entry, date)
          else onMove(d.entry, date)
        }}
      >
        <div className="list-cell-head">
          {view === 'day' ? (
            <span className="list-cell-date">{formatDate(date, { withYear: true })}</span>
          ) : onPickDay ? (
            <button
              type="button"
              className={`list-cell-date ${date === today ? 'today' : ''}`}
              title={`Open ${formatDate(date)}`}
              onClick={(e) => { e.stopPropagation(); onPickDay(date) }}
            >
              {dayNumber}
            </button>
          ) : (
            <span className={`list-cell-date ${date === today ? 'today' : ''}`}>{dayNumber}</span>
          )}
          {/* The month's name on its first day, so a grid that reaches into the next month
              says where the boundary is. */}
          {view !== 'day' && isFirst && (
            <span className="list-cell-month">{formatShort(date).split(' ')[1]}</span>
          )}
          {holiday?.observed && (
            <span className="mcal-stat" title={holiday.name}>STAT</span>
          )}
          {entries.length > 0 && <span className="list-cell-count">{entries.length}</span>}
        </div>
        <div className="list-cell-stack">{entries.map(renderChip)}</div>
        {/* The board's + (see .cell-add): the full height of the day on its right edge,
            always showing — on an empty day as well. The whole day is clickable too, but a
            + in the same place on every day is how somebody finds where to add without
            first having to learn that a blank day is a button. It replaced a faint + that
            appeared in an empty day's corner on hover, which only helped once found. */}
        {writable && (
          <button
            type="button"
            className="cell-add list-cell-add"
            title="Add somebody"
            aria-label={`Add somebody to ${formatDate(date)}`}
            onClick={(e) => { e.stopPropagation(); openAdd(date, e.currentTarget.closest('td')) }}
          >
            <Icon name="plus" size={13} strokeWidth={2.4} />
          </button>
        )}
      </td>
    )
  }

  const menuEntryName = menu?.entry ? nameOf(menu.entry) : ''

  return (
    <div className={`board-wrap list-wrap list-view-${view}`}>
      <table
        className="board list-board"
        // The list's colour washes its days and its chips, a shade stronger on the chips —
        // the same pair of tints a crew band gets on the board.
        style={
          colour
            ? { '--list': colour, '--list-tint': tintOf(colour, 0.9), '--list-tint-strong': tintOf(colour, 0.8) }
            : undefined
        }
        {...gridKeys}
      >
        {view !== 'day' && (
          <thead>
            <tr>
              {weeks[0].map((d) => (
                <th key={d} className="week-day-head list-day-head">
                  <span className="week-day-btn">
                    <span className="week-day-name">{weekdayName(d)}</span>
                  </span>
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {weeks.map((row) => (
            <tr key={row[0]} className="list-week-row">{row.map(renderCell)}</tr>
          ))}
        </tbody>
      </table>

      {editCtx && (
        <ListEntryEditor
          // Keyed by what it is editing, so opening another day or another chip starts clean.
          key={editCtx.mode === 'add' ? `add:${editCtx.date}` : `edit:${editCtx.entry.id}`}
          anchorRef={anchorRef}
          mode={editCtx.mode}
          date={editCtx.date}
          people={editCtx.mode === 'add' ? availableOn(editCtx.date) : []}
          entry={editCtx.entry || null}
          onAdd={({ userId, note }) => onAdd({ date: editCtx.date, userId, note })}
          onSave={(note) => onSaveNote(editCtx.entry, note)}
          onRemove={() => onRemove(editCtx.entry)}
          onClose={closeEditor}
        />
      )}

      {menu && (
        <ContextMenu x={menu.x} y={menu.y} onClose={() => setMenu(null)}>
          {menu.kind === 'chip' && (
            <>
              <ContextMenuLabel>{menuEntryName} · {formatDate(menu.date)}</ContextMenuLabel>
              <ContextMenuItem icon="copy" onClick={() => { copyChip(menu.entry); setMenu(null) }}>
                Copy
              </ContextMenuItem>
              <ContextMenuItem icon="cut" onClick={() => { copyChip(menu.entry, true); setMenu(null) }}>
                Cut
              </ContextMenuItem>
              <ContextMenuItem
                icon="note"
                onClick={() => { openEdit(menu.entry, chipEl(menu.entry.id)); setMenu(null) }}
              >
                {menu.entry.note ? 'Edit note' : 'Add a note'}
              </ContextMenuItem>
              <ContextMenuSeparator />
              <ContextMenuItem icon="trash" onClick={() => { onRemove(menu.entry); setMenu(null) }}>
                Remove from this day
              </ContextMenuItem>
            </>
          )}
          {menu.kind === 'day' && (
            <>
              <ContextMenuLabel>{formatDate(menu.date)}</ContextMenuLabel>
              {clip && (
                <ContextMenuItem
                  icon="paste"
                  disabled={!canPasteOn(menu.date)}
                  onClick={() => { paste(menu.date); setMenu(null) }}
                >
                  Paste {clip.name}
                </ContextMenuItem>
              )}
              <ContextMenuItem
                icon="plus"
                onClick={() => { openAdd(menu.date, findCell(menu.date)); setMenu(null) }}
              >
                Add somebody
              </ContextMenuItem>
            </>
          )}
        </ContextMenu>
      )}
    </div>
  )
}
